import apiClient from "../../apiClient";
import { API_ENDPOINTS } from "../../config";

const orderService = {
  /**
   * Create a new order
   */
  createOrder: async (orderData) => {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.CRM.ORDERS,
        orderData
      );

      return {
        success: response.responseCode === 201 || response.responseCode === 200,
        order: response.data,
        responseMessage: response.responseMessage || 'Order created successfully'
      };

    } catch (error) {
      
      const errorMessage = error.response?.data?.responseMessage || error.response?.data?.error || error.message || "Failed to create order";
      return {
        success: false,
        error: errorMessage,
      };
    }
  },

  /**
   * Get order by ID
   */
  getOrderById: async (orderId) => {
    try {
      const response = await apiClient.get(`${API_ENDPOINTS.CRM.ORDERS}/${orderId}`);

      return {
        success: response.responseCode === 200,
        order: response.data
      };

    } catch (error) {
      
      return {
        success: false,
        error: error.response?.data?.responseMessage || "Failed to retrieve order"
      };
    }
  },

  /**
   * Get all orders
   */
  getOrders: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append("search", filters.search);
      if (filters.status) params.append("status", filters.status);
      if (filters.startDate) params.append("startDate", filters.startDate);
      if (filters.endDate) params.append("endDate", filters.endDate);
      if (filters.deliveryStatus) params.append("deliveryStatus", filters.deliveryStatus);
      if (filters.page) params.append("page", filters.page);
      if (filters.limit) params.append("limit", filters.limit);

      const response = await apiClient.get(
        `${API_ENDPOINTS.CRM.ORDERS}?${params.toString()}`
      );

      return {
        success: response.responseCode === 200,
        orders: Array.isArray(response.data) ? response.data : response.data?.orders || [],
        pagination: response.data?.pagination || {}
      };

    } catch (error) {
      
      return {
        success: false,
        orders: [],
        error: error.response?.data?.responseMessage || "Failed to retrieve orders"
      };
    }
  },

  /**
   * Update order
   */
  updateOrder: async (orderId, orderData) => {
    try {
      const response = await apiClient.patch(
        `${API_ENDPOINTS.CRM.ORDERS}/${orderId}`,
        orderData
      );

      return {
        success: response.responseCode === 200,
        order: response.data
      };

    } catch (error) {
      
      return {
        success: false,
        error: error.response?.data?.responseMessage || "Failed to update order"
      };
    }
  },

  /**
   * Get orders by customer ID with pagination
   */
  getOrdersByCustomer: async (customerId, params = {}) => {
    try {
      const { page = 1, limit = 10 } = params;
      const queryParams = new URLSearchParams();
      queryParams.append('page', page);
      queryParams.append('limit', limit);

      const response = await apiClient.get(
        `${API_ENDPOINTS.CRM.ORDERS}/by-customer/${customerId}?${queryParams.toString()}`
      );

      return {
        success: response.responseCode === 200,
        data: response.data?.orders || [],
        pagination: response.data?.pagination || {}
      };

    } catch (error) {
      
      return {
        success: false,
        error: error.response?.data?.responseMessage || "Failed to retrieve orders",
        data: [],
        pagination: {}
      };
    }
  },

  /**
   * Delete an order by ID
   */
  deleteOrder: async (orderId) => {
    try {
      const response = await apiClient.delete(`${API_ENDPOINTS.CRM.ORDERS}/${orderId}`);

      return {
        success: response.responseCode === 200 || response.responseCode === 204,
        message: response.responseMessage || "Order deleted successfully"
      };

    } catch (error) {
      
      return {
        success: false,
        error: error.response?.data?.responseMessage || "Failed to delete order"
      };
    }
  },

  /**
   * Sync shipment status from Shipway
   */
  syncShipments: async () => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.CRM.SHIPMENTS.SYNC);

      return {
        success: response.success === true,
        message: response.responseMessage || response.message || "Sync completed",
        results: response.results
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to sync shipments"
      };
    }
  },
  
  /**
   * Get shipment sync status
   */
  getSyncStatus: async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.CRM.SHIPMENTS.SYNC_STATUS);
      return {
        success: true,
        isRunning: response.isRunning,
        startTime: response.startTime
      };
    } catch (error) {
      
      return { success: false, isRunning: false };
    }
  }
};

export default orderService;

